import type { VNode } from 'vue'

import type { I18nSlots } from '../types/i18n'

/**
 * This function transforms a given message into an array of strings and/or vnodes.
 *
 * @example
 * replacePlaceholderWithSlots('Hello {world}', …) // ['Hello, ', VNode]
 *
 * @example
 * replacePlaceholderWithSlots ('My name is {first} {last}.', …) // ['My name is ', VNode, ' ', VNode, '.']
 *
 * 1. To take advantage of HTML (and components) interpolation, we need to return
 *    an array, and let the Vue compiler do its work. We can't just compute a
 *    string to display in our templates.
 *
 * 2. Note that by doing so, we gain everything Vue has to offer. We can use CSS,
 *    JS and whatever in our translations. We can even use Vue components as-is.
 *
 * 3. We also prevent a huge security breach: XSS issues. Because Vue does the work
 *    for us, we no longer need to manually use `v-html`. So, an attacker would
 *    not be able to inject arbitrary tags (and attributes) in our pages.
 *
 */
export function replacePlaceholderWithSlots<Message extends string>(
  message: Message,
  slots: I18nSlots,
): (VNode | string)[] {
  const tokens: (VNode | string)[] = []

  let text = ''
  let position = 0

  while (position < message.length) {
    const character = message[position]

    switch (character) {
      case '{':
        tokens.push(text)
        text = ''
        break

      case '}':
        {
          // Placeholders follow ICU MessageFormat syntax, which allows a format
          // specifier after the placeholder name (e.g. `{useTerms, html}`).
          // Strip the format specifier to get the slot name.
          // @see https://unicode-org.github.io/icu/userguide/format_parse/messages/
          const slotKey = text.split(',')[0].trim() as keyof typeof slots

          tokens.push(slots[slotKey])
        }
        text = ''
        break

      default:
        text += character
        break
    }

    position += 1
  }

  // Do not forget to push the rest of the message when we reach the end.
  tokens.push(text)

  // Since we do not validate the items pushed into the `tokens` array, we might
  // end up with falsy values (undefined, empty strings…). To avoid polluting
  // our Vue templates with those, let's remove them at the end.
  return tokens.filter(Boolean)
}
